/**
 * Tactile press class + a short haptic pulse on purchase/action controls.
 * Does not call preventDefault — clicks and form submits stay intact.
 *
 * Haptics fire only after a completed tap (finger up, little movement).
 * Finger-down that turns into a scroll must not vibrate.
 *
 * Chrome blocks Vibration API in cross-origin iframes (theme editor preview).
 * Pixel / Android also ignore very short one-shots like vibrate(10).
 */
(() => {
  const PRESS_SELECTOR = [
    '.button',
    '.button-secondary',
    '.button-custom',
    '.add-to-cart-button',
    '.header-actions__action',
    '.header-inline-nav__link',
    '.centered-menu__link',
    '.product-card',
    '.collection-card',
    '.collection-links__link',
    'button[type="submit"]',
    'input[type="submit"]',
    '.shopify-payment-button__button',
  ].join(',');

  const HAPTIC_SELECTOR = [
    '.button',
    '.button-secondary',
    '.button-custom',
    '.add-to-cart-button',
    '.header-actions__action',
    'button[type="submit"]',
    'input[type="submit"]',
    '.shopify-payment-button__button',
  ].join(',');

  const TAP_SLOP_PX = 12;
  const DRAG_INTENT_EVENT = 'theme:drag-intent';
  const pressed = new Set();
  let lastPulse = 0;
  let gesture = null;

  function closestControl(target, selector) {
    if (!(target instanceof Element)) return null;
    const el = target.closest(selector);
    if (!el) return null;
    if (el.matches(':disabled, [aria-disabled="true"]')) return null;
    return el;
  }

  function canVibrateHere() {
    try {
      return window.self === window.top;
    } catch {
      return false;
    }
  }

  function pulseHaptic() {
    const now = Date.now();
    if (now - lastPulse < 80) return;
    lastPulse = now;

    const vibrate = navigator.vibrate || navigator.webkitVibrate;
    if (typeof vibrate !== 'function') return;
    if (!canVibrateHere()) return;

    try {
      const android = /Android/i.test(navigator.userAgent || '');
      /* 10ms is a no-op on Pixel 9 / 9a. ~50ms is still a tap but the motor fires. */
      if (android) {
        vibrate.call(navigator, 50);
      } else {
        vibrate.call(navigator, 10);
      }
    } catch {
      /* Unsupported or blocked — ignore. */
    }
  }

  function releaseAll() {
    pressed.forEach((el) => el.classList.remove('is-pressed'));
    pressed.clear();
  }

  function clearGesture() {
    gesture = null;
    releaseAll();
  }

  function movementExceeded(event, start) {
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    return dx * dx + dy * dy > TAP_SLOP_PX * TAP_SLOP_PX;
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      const isTouch = event.pointerType === 'touch' || event.pointerType === 'pen';
      if (!isTouch && event.button != null && event.button !== 0) return;

      const el = closestControl(event.target, PRESS_SELECTOR);
      if (!el) {
        gesture = null;
        return;
      }

      el.classList.add('is-pressed');
      pressed.add(el);

      gesture = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        isTouch,
        haptic: Boolean(closestControl(event.target, HAPTIC_SELECTOR)),
        moved: false,
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      if (gesture.moved) return;
      if (!movementExceeded(event, gesture)) return;

      gesture.moved = true;
      releaseAll();
    },
    { passive: true }
  );

  // Slideshow owns the exact drag threshold. Once it claims the gesture,
  // release card press feedback and prevent a drag from producing haptics.
  document.addEventListener(DRAG_INTENT_EVENT, (event) => {
    if (!gesture) return;
    const pointerId = event instanceof CustomEvent ? event.detail?.pointerId : undefined;
    if (pointerId !== undefined && pointerId !== gesture.id) return;
    gesture.moved = true;
    releaseAll();
  });

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!gesture || event.pointerId !== gesture.id) {
        clearGesture();
        return;
      }

      const tap =
        gesture.isTouch &&
        gesture.haptic &&
        !gesture.moved &&
        !movementExceeded(event, gesture);

      clearGesture();
      if (tap) pulseHaptic();
    },
    { passive: true }
  );

  document.addEventListener(
    'pointercancel',
    (event) => {
      if (gesture && event.pointerId === gesture.id) clearGesture();
    },
    { passive: true }
  );
})();
